/**
 * Alta y administración de usuarios.
 *
 * El problema que resuelve este módulo: `createUserWithEmailAndPassword`
 * **inicia sesión** con el usuario recién creado. Llamado desde la sesión del
 * administrador, lo expulsaría de su propia cuenta a mitad del trámite.
 *
 * La salida es levantar una segunda instancia de Firebase, sólo para esto,
 * con persistencia en memoria: crea la cuenta, devuelve el identificador y se
 * cierra. La sesión del administrador ni se entera. El perfil (con el rol) se
 * escribe desde la instancia principal, que es la que tiene los permisos.
 *
 * Lo que no se puede hacer desde aquí: borrar una cuenta de Authentication.
 * Eso exige credenciales de servidor. Por eso las cuentas se **desactivan**
 * (`activo: false`), y las reglas de Firestore le niegan todo a quien esté
 * desactivado.
 */

import { initializeApp, deleteApp, type FirebaseApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  initializeAuth,
  inMemoryPersistence,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import { collection, doc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { auth, configFirebase, db, COL, SIN_CONFIGURAR } from './firebase';
import type { Perfil, Rol } from '@/hooks/useAuth';

export interface DatosAlta {
  nombre: string;
  email: string;
  clave: string;
  rol: Rol;
  plantaId: string | null;
}

/** Traduce los códigos de Firebase a algo que se entienda. */
function mensaje(e: unknown): string {
  const codigo = (e as { code?: string })?.code ?? '';
  switch (codigo) {
    case 'auth/email-already-in-use':
      return 'Ya existe una cuenta con ese correo.';
    case 'auth/invalid-email':
      return 'El correo no tiene un formato válido.';
    case 'auth/weak-password':
      return 'La contraseña es muy corta: mínimo 6 caracteres.';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa la red e inténtalo de nuevo.';
    case 'permission-denied':
      return 'Tu usuario no tiene permiso para esto. ¿Publicaste la última versión de las reglas?';
    default:
      return e instanceof Error ? e.message : 'No se pudo completar la operación.';
  }
}

/** Todos los perfiles. Sólo un administrador puede leerlos. */
export async function listarUsuarios(): Promise<Perfil[]> {
  if (SIN_CONFIGURAR || !db) return [];
  try {
    const snap = await getDocs(collection(db, COL.usuarios));
    return snap.docs
      .map((d) => {
        const x = d.data() as Partial<Perfil>;
        return {
          uid: d.id,
          email: x.email ?? '',
          nombre: x.nombre ?? '',
          rol: (x.rol as Rol) ?? 'LECTOR',
          plantaId: x.plantaId ?? null,
          activo: x.activo ?? true,
        };
      })
      .sort((a, b) => (a.nombre || a.email).localeCompare(b.nombre || b.email, 'es'));
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/**
 * Crea la cuenta y su perfil. Devuelve el perfil ya escrito.
 * La sesión de quien la ejecuta queda intacta.
 */
export async function crearUsuario(d: DatosAlta): Promise<Perfil> {
  if (SIN_CONFIGURAR || !db) throw new Error('Firebase no está configurado.');

  const email = d.email.trim().toLowerCase();
  const nombre = d.nombre.trim();
  if (!nombre) throw new Error('Falta el nombre.');
  if (d.clave.length < 6) throw new Error('La contraseña es muy corta: mínimo 6 caracteres.');

  let secundaria: FirebaseApp | null = null;
  try {
    secundaria = initializeApp(configFirebase, `alta-${Date.now()}`);
    // En memoria: la sesión del nuevo usuario no toca el almacenamiento del
    // navegador ni pisa la del administrador.
    const authAlta = initializeAuth(secundaria, { persistence: inMemoryPersistence });

    const cred = await createUserWithEmailAndPassword(authAlta, email, d.clave);
    const uid = cred.user.uid;
    await signOut(authAlta);

    const perfil: Perfil = {
      uid,
      email,
      nombre,
      rol: d.rol,
      plantaId: d.plantaId,
      activo: true,
    };
    // Se escribe desde la instancia principal: es la que va autenticada como
    // administrador, y es lo que las reglas exigen.
    await setDoc(doc(db, COL.usuarios, uid), {
      ...perfil,
      creadoEn: new Date().toISOString(),
      creadoPor: auth?.currentUser?.uid ?? null,
    });
    return perfil;
  } catch (e) {
    throw new Error(mensaje(e));
  } finally {
    if (secundaria) await deleteApp(secundaria).catch(() => {});
  }
}

/** Cambia el rol de un usuario. */
export async function cambiarRol(uid: string, rol: Rol, plantaId: string | null): Promise<void> {
  if (!db) throw new Error('Firebase no está configurado.');
  try {
    await updateDoc(doc(db, COL.usuarios, uid), {
      rol,
      plantaId,
      actualizadoEn: new Date().toISOString(),
      actualizadoPor: auth?.currentUser?.uid ?? null,
    });
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Activa o desactiva el acceso. Desactivado equivale a sin permisos. */
export async function cambiarActivo(uid: string, activo: boolean): Promise<void> {
  if (!db) throw new Error('Firebase no está configurado.');
  try {
    await updateDoc(doc(db, COL.usuarios, uid), {
      activo,
      actualizadoEn: new Date().toISOString(),
      actualizadoPor: auth?.currentUser?.uid ?? null,
    });
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Manda el correo para que el usuario elija su propia contraseña. */
export async function enviarRestablecer(email: string): Promise<void> {
  if (!auth) throw new Error('Firebase no está configurado.');
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Contraseña provisoria legible, para dictarla por teléfono. */
export function claveSugerida(): string {
  const silabas = ['ma', 'ru', 'te', 'lo', 'si', 'pa', 'ne', 'do', 'va', 'ki'];
  const al = (n: number) => Math.floor(Math.random() * n);
  const s = () => silabas[al(silabas.length)] ?? 'xa';
  return s() + s() + s() + '-' + String(100 + al(900));
}
